import { VesselCardEvaluationHistoryProps } from "../models/vessel-card-evaluation.models";
import {
  formatVesselCardEvaluationFileSize,
  getVesselCardEvaluationNotificationLabel,
  getVesselCardEvaluationReasonLabel,
} from "../utils/vessel-card-evaluation.utils";

// Makes saved request text and supporting files available in the vessel card for DVP review.
// Assumes the latest 20 requests are sufficient for this first release; each request retains its own attachment downloads.
const VesselCardEvaluationHistory = ({ previewOnly = false, requests, isLoading, error, downloadError, downloadingAttachmentId, onRefresh, onDownload }: VesselCardEvaluationHistoryProps) => (
  <section className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700" data-test="vessel-card-evaluation-history">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="text-sm font-semibold">{previewOnly ? "Sample evaluation requests" : "Recent evaluation requests"}</h3>
      <button type="button" disabled={isLoading} onClick={onRefresh} data-test="vessel-card-evaluation-history-refresh-button" className="rounded-md border px-3 py-1 text-xs disabled:opacity-50">
        {isLoading ? "Refreshing…" : "Refresh"}
      </button>
    </div>
    <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{previewOnly ? "Demonstration attachment metadata. Downloads are unavailable in preview mode." : "Latest 20 requests for this vessel."}</p>
    {error && <p role="alert" data-test="vessel-card-evaluation-history-error" className="mb-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
    {downloadError && <p role="alert" data-test="vessel-card-evaluation-download-error" className="mb-3 text-sm text-red-700 dark:text-red-300">{downloadError}</p>}
    {isLoading && !requests.length && <p role="status" className="text-sm text-gray-500">Loading requests…</p>}
    {!isLoading && !error && !requests.length && <p data-test="vessel-card-evaluation-history-empty" className="text-sm text-gray-500">No evaluation requests have been saved for this vessel.</p>}
    <div className="space-y-3">
      {requests.map((request) => (
        <details key={request.requestId} data-test={`vessel-card-evaluation-history-request-${request.requestId}`} className="rounded-lg border border-gray-200 p-3 dark:border-gray-600">
          <summary className="cursor-pointer text-sm font-semibold">
            {getVesselCardEvaluationReasonLabel(request.reason)} · {new Date(request.createdAt).toLocaleString()}
          </summary>
          <dl className="mt-3 space-y-1 break-words text-xs text-gray-600 dark:text-gray-300">
            <div><dt className="inline font-semibold">Reference: </dt><dd className="inline">{request.requestId}</dd></div>
            <div><dt className="inline font-semibold">Requested by: </dt><dd className="inline">{request.requestedBy}</dd></div>
            <div><dt className="inline font-semibold">Notification: </dt><dd className="inline">{previewOnly ? "Sample only; DVP was not notified." : getVesselCardEvaluationNotificationLabel(request.notificationStatus)}</dd></div>
          </dl>
          <p className="mt-3 whitespace-pre-wrap break-words text-sm" data-test={`vessel-card-evaluation-history-details-${request.requestId}`}>
            {request.additionalDetails || "No additional details provided."}
          </p>
          {request.attachments.length ? (
            <ul className="mt-3 space-y-2" aria-label="Supporting attachments">
              {request.attachments.map((attachment) => (
                <li key={attachment.attachmentId} className="flex items-center justify-between gap-3 text-xs">
                  <span className="min-w-0 break-all">{attachment.fileName} ({formatVesselCardEvaluationFileSize(attachment.sizeBytes)})</span>
                  <button type="button" disabled={previewOnly || downloadingAttachmentId !== null} onClick={() => onDownload(request.requestId, attachment.attachmentId)} data-test={`vessel-card-evaluation-download-${attachment.attachmentId}-button`} className="shrink-0 rounded border px-2 py-1 disabled:opacity-50">
                    {previewOnly ? "Preview only" : downloadingAttachmentId === attachment.attachmentId ? "Preparing…" : "Download"}
                  </button>
                </li>
              ))}
            </ul>
          ) : <p className="mt-3 text-xs text-gray-500">No supporting files.</p>}
        </details>
      ))}
    </div>
  </section>
);

export default VesselCardEvaluationHistory;
